import { useState } from 'react'
import { CartLinesQueryVariables, useCartLinesQuery } from '@graphql/queries/__generated__/cart.generated'
import { SHOW_MORE_OPTIONS } from '@components/ShowMoreButton'

/**
 * Cart lines list loaded in "Show More" chunks: each chunk is fetched by offset and appended to the shown ones
 * (see `cartLinesOffsetPagination` in apolloClient.ts).
 */
const useCartLinesShowMore = (variables: Omit<CartLinesQueryVariables, 'limit' | 'offset'>, skip: boolean) => {
  // How many lines each "Show More" click appends.
  const [step, setStep] = useState(SHOW_MORE_OPTIONS[0])
  // Size of the whole shown range. It stays in the query variables so that refetchQueries: ['CartLines'] after a
  // cart mutation reloads every shown line, not only the first chunk.
  const [limit, setLimit] = useState(SHOW_MORE_OPTIONS[0])
  const [loadingMore, setLoadingMore] = useState(false)
  const cartLinesQuery = useCartLinesQuery({
    // Once "Show More" was used the grown range is already in the cache (fetchMore merged it), so the new limit
    // must be read from the cache - useQuery would otherwise refetch the whole range on the variables change.
    fetchPolicy: limit > SHOW_MORE_OPTIONS[0] ? 'cache-first' : 'cache-and-network',
    nextFetchPolicy: () => 'cache-first',
    skip,
    variables: { ...variables, limit, offset: 0 },
  })
  // While the first chunk reloads keep showing the previous data.
  const data = cartLinesQuery.data ?? cartLinesQuery.previousData
  const lines = data?.cartLines.results || []
  const totalCount = data?.cartLines.totalCount || 0

  const showMore = async () => {
    const newLimit = lines.length + step
    setLoadingMore(true)
    try {
      const { data: moreData } = await cartLinesQuery.fetchMore({ variables: { offset: lines.length, limit: step } })
      setLimit(newLimit)
      if (moreData.cartLines.totalCount !== totalCount) {
        // Lines were added or removed outside this tab since the shown range loaded, so the offset is stale -
        // reload the whole range.
        await cartLinesQuery.refetch({ limit: newLimit, offset: 0 })
      }
    } finally {
      setLoadingMore(false)
    }
  }

  return {
    lines,
    totalCount,
    hasMore: lines.length < totalCount,
    isLoading: cartLinesQuery.loading,
    loadingMore,
    step,
    setStep,
    showMore,
  }
}

export default useCartLinesShowMore
